import { IconQuestionCircleOutlined } from "@suse-o11y/icons";
import type { Record } from "@suse-o11y/record";
import { useMonitor } from "@suse-o11y/signal";
import type { FormUnit } from "@suse-o11y/signal/form";
import React from "react";

import { FormField } from "~/components/form/field";
import { FormLabel } from "~/components/form/label";
import { FormFieldReset } from "~/components/form/reset";
import { Input } from "~/components/input";
import { Select } from "~/components/select";
import { SelectContent } from "~/components/select/content";
import { SelectItem } from "~/components/select/item";
import { SelectTrigger } from "~/components/select/trigger";
import { SelectValue } from "~/components/select/value";
import { Switch } from "~/components/switch";
import { SwitchLabel } from "~/components/switch/label";
import { Tooltip } from "~/components/tooltip";
import { TooltipContent } from "~/components/tooltip/content";
import { TooltipTrigger } from "~/components/tooltip/trigger";

import type { DashboardListVariableSort, DashboardVariableSource } from "../../../common/dashboard-spec";

import { type FormState, SORT_OPTIONS, SOURCE_OPTIONS, Sort } from "./_form-state";
import { MetricLabelValuesVariableForm } from "./_metric-label-values-variable";
import { MetricPromQLVariableForm } from "./_metric-promql-variable";
import { SeriesSelectors } from "./_series-selectors";
import { StaticListVariableForm } from "./_static-list-variable";

const SOURCE_TO_LABEL = {
	MetricLabelNames: "Metric label names",
	MetricLabelValues: "Metric label values",
	MetricPromQL: "Metric PromQL",
	StaticList: "Static list",
} satisfies Record<DashboardVariableSource>;

const SORT_TO_LABEL = {
	none: "None",
	"alphabetical-asc": "Alphabetical (A-Z)",
	"alphabetical-desc": "Alphabetical (Z-A)",
	"numerical-asc": "Numerical (0-9)",
	"numerical-desc": "Numerical (9-0)",
	"alphabetical-ci-asc": "Alphabetical Case Insensitive (A-Z)",
	"alphabetical-ci-desc": "Alphabetical Case Insensitive (Z-A)",
} satisfies Record<DashboardListVariableSort>;

export interface VariableFormProps {
	isEditing: boolean;
	name: FormUnit<string, unknown, string>;
	state: FormState;
}

export const VariableForm: React.FC<VariableFormProps> = ({ isEditing, name, state }) => {
	const monitor = useMonitor();
	const activeSource = state.fields.source.getActiveBranch(monitor);
	const customAllValue = state.fields.allValue.getEnabledElement(monitor);

	return (
		<>
			<FormField state={state.fields.source.active}>
				<FormLabel asChild>
					<div>
						<FormLabel>Source</FormLabel>
						{isEditing && <FormFieldReset />}
					</div>
				</FormLabel>

				{(fieldMonitor, field) => (
					<Select
						name="dashboard-list-variable-source"
						onValueChange={(input) => field.setInput(input)}
						value={field.getInput(fieldMonitor)}
					>
						<SelectTrigger className="w-full">
							<SelectValue />
						</SelectTrigger>

						<SelectContent className="z-drawer!">
							{SOURCE_OPTIONS.map((option) => (
								<SelectItem key={option} value={option}>
									{SOURCE_TO_LABEL[option]}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}
			</FormField>

			{activeSource?.kind === "MetricLabelNames" && <SeriesSelectors state={activeSource.value} />}

			{activeSource?.kind === "MetricLabelValues" && <MetricLabelValuesVariableForm state={activeSource.value} />}

			{activeSource?.kind === "MetricPromQL" && <MetricPromQLVariableForm name={name} state={activeSource.value} />}

			{activeSource?.kind === "StaticList" && (
				<StaticListVariableForm
					canReorder={state.fields.sort.getOutput(monitor) === Sort.None}
					state={activeSource.value}
				/>
			)}

			<FormField state={state.fields.sort}>
				<FormLabel asChild>
					<div>
						<FormLabel>Sort</FormLabel>
						<FormFieldReset />
					</div>
				</FormLabel>

				{(fieldMonitor, field) => (
					<Select
						name="dashboard-list-variable-sort"
						onValueChange={(input) => field.setInput(input)}
						value={field.getInput(fieldMonitor)}
					>
						<SelectTrigger className="w-full">
							<SelectValue />
						</SelectTrigger>

						<SelectContent className="z-drawer!">
							{SORT_OPTIONS.map((option) => (
								<SelectItem key={option} value={option}>
									{SORT_TO_LABEL[option]}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}
			</FormField>

			<FormField state={state.fields.allowMultiple}>
				<FormLabel>Variable Selector</FormLabel>

				{(fieldMonitor, field) => (
					<div className="flex w-full items-center gap-2">
						<SwitchLabel className="flex items-center gap-2">
							<Switch
								checked={field.getInput(fieldMonitor)}
								name="dashboard-list-variable-allow-multiple"
								onCheckedChange={(checked) => field.setInput(checked)}
							/>
							Allow multiple values
						</SwitchLabel>

						<Tooltip delayDuration={300}>
							<TooltipTrigger className="cursor-help!">
								<IconQuestionCircleOutlined />
							</TooltipTrigger>

							<TooltipContent>Enables multiple values to be selected at the same time</TooltipContent>
						</Tooltip>

						<div className="flex flex-1 justify-end">
							<FormFieldReset />
						</div>
					</div>
				)}
			</FormField>

			<FormField state={state.fields.allValue.enabled}>
				{(fieldMonitor, field) => (
					<div className="flex w-full items-center gap-2">
						<SwitchLabel className="flex items-center gap-2">
							<Switch
								checked={field.getInput(fieldMonitor)}
								name="dashboard-list-variable-allow-all"
								onCheckedChange={(checked) => field.setInput(checked)}
							/>
							Enable "Include everything" option
						</SwitchLabel>

						<Tooltip delayDuration={300}>
							<TooltipTrigger className="cursor-help!">
								<IconQuestionCircleOutlined />
							</TooltipTrigger>

							<TooltipContent>Enables an option to include all variable values</TooltipContent>
						</Tooltip>

						<div className="flex flex-1 justify-end">
							<FormFieldReset />
						</div>
					</div>
				)}
			</FormField>

			{customAllValue && (
				<FormField state={customAllValue}>
					<FormLabel asChild>
						<div className="gap-2">
							<FormLabel>"Include everything" Value</FormLabel>
							<Tooltip delayDuration={300}>
								<TooltipTrigger className="cursor-help!">
									<IconQuestionCircleOutlined />
								</TooltipTrigger>

								<TooltipContent>When "Include everything" is selected, this value will be used</TooltipContent>
							</Tooltip>

							<div className="flex flex-1 justify-end">
								<FormFieldReset />
							</div>
						</div>
					</FormLabel>

					{(fieldMonitor, field) => (
						<Input
							name="dashboard-list-variable-all-value"
							onChange={(event) => field.setInput(event.target.value)}
							placeholder="by default it joins all options, e.g. (value-1|value-2|value-3)"
							value={field.getInput(fieldMonitor)}
						/>
					)}
				</FormField>
			)}
		</>
	);
};
